import { Button, Card, Result, Spin, Steps, Typography, message } from "antd";
import useSurveyResponseProcessor from "hooks/use-survey-response-processor";
import React, { useState } from "react";
import { SurveyModel } from "survey-core";
import { Survey } from "survey-react-ui";
import ExcelDataExtractor from "../../../components/ExcelDataExtractor/ExcelDataExtractor";
import ExcelReuploadModal from "../../../components/ExcelReuploadModal/ExcelReuploadModal";
import SurveyResponseProcessor from "../../../components/SurveyResponseProcessor/SurveyResponseProcessor";
import { surveyThemeConfig } from "../../../config/survey-theme-config";
import "../../../styles/survey-theme.scss";
import { json } from "../default_survey";
import "./SmartBEA.scss";

const { Paragraph, Title } = Typography;
const { Step } = Steps;

interface OnboardingComponentProps {
  locationId: string;
}

const OnboardingComponent: React.FC<OnboardingComponentProps> = ({ locationId }) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [survey, setSurvey] = useState<SurveyModel | null>(null);
  const [surveyData, setSurveyData] = useState<any>(null);
  const [showExcelExtractor, setShowExcelExtractor] = useState<boolean>(false);
  const [combinedData, setCombinedData] = useState<any>(null);
  // Set a fixed userId for the existing user without creating a new one
  const [userId] = useState<string>("existing-user-id");
  const [isStartScreen, setIsStartScreen] = useState<boolean>(true);
  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const [excelError, setExcelError] = useState<string | null>(null);
  const { loading } = useSurveyResponseProcessor();

  // Initialize the survey with the predefined JSON
  React.useEffect(() => {
    // Apply theme CSS variables only to the survey form
    const surveyContainer = document.querySelector(".onboarding-component");
    if (surveyContainer) {
      Object.entries(surveyThemeConfig.cssVariables).forEach(([key, value]) => {
        (surveyContainer as HTMLElement).style.setProperty(key, value);
      });
    }

    // Create the survey model
    try {
      const newSurvey = new SurveyModel(json);

      // Handle survey completion
      newSurvey.onComplete.add((sender) => {
        const completedData = sender.data;
        setSurveyData(completedData);

        // Check if the completed data contains location_consumption with Excel file
        if (
          completedData.location_consumption &&
          Array.isArray(completedData.location_consumption) &&
          completedData.location_consumption.length > 0
        ) {
          // Store the survey data and show the Excel extractor
          setShowExcelExtractor(true);
        } else {
          // Skip email verification and go directly to processing
          setCombinedData(completedData);
          setCurrentStep(1);
        }
      });

      // Set survey width
      newSurvey.width = "100%";
      newSurvey.maxWidth = "800px";
      setSurvey(newSurvey);
    } catch (error) {
      message.error("Error initializing the survey form");
    }
  }, []);

  const handleStartSurvey = () => {
    // Hide the start screen and show the survey
    setIsStartScreen(false);
  };

  const handleProcessingComplete = (result: { locationId: string; userId: string }) => {
    message.success(`Successfully updated location information for ${result.locationId}`);
    // Refresh the page to reflect the updated onboarding status
    window.location.reload();
  };

  const handleExcelDataConfirm = (extractedData: any) => {
    if (surveyData) {
      // Create a copy of the survey data
      const updatedData = { ...surveyData };

      // Check if this is a reuploaded file
      const isReuploadedFile = extractedData.isReuploaded === true;

      // For reuploaded files, completely clear any existing Excel data
      if (isReuploadedFile) {
        // Clean up any data that might have been extracted from the invalid file
        if (updatedData.extractedExcelData) {
          delete updatedData.extractedExcelData;
        }

        if (updatedData.originalExcelData) {
          delete updatedData.originalExcelData;
        }

        if (updatedData.consumptionFileURL) {
          delete updatedData.consumptionFileURL;
        }
      }

      // Add the extracted data to the survey data, ensuring no duplicates
      if (Array.isArray(extractedData.extractedExcelData)) {
        // Create a Map to deduplicate by month+year+data_type+consumption type
        const uniqueRecordsMap = new Map();

        extractedData.extractedExcelData.forEach((record: any) => {
          // Create a unique key based on month, year, data_type, and consumption type
          const month = record.month || "";
          const year = record.year || "";
          const dataType = record.data_type || "";

          // Determine consumption type based on which fields have values
          let consumptionType = "";
          if (record.electricity_consumption) consumptionType = "electricity";
          else if (record.water_consumption) consumptionType = "water";
          else if (record.gas_consumption) consumptionType = "gas";
          else if (record.waste_consumption) consumptionType = "waste";

          // Include measurement unit in the key if available
          let unitInfo = "";
          if (record.electricity_consumption) unitInfo += "kWh";
          if (record.water_consumption) unitInfo += "m3";
          if (record.gas_consumption) unitInfo += "m3-gas";
          if (record.waste_consumption) unitInfo += "MT";

          // Create a detailed unique key
          const uniqueKey = `${month}-${year}-${dataType}-${consumptionType}-${unitInfo}`;

          // Only keep the most recent entry for each unique key
          uniqueRecordsMap.set(uniqueKey, record);
        });

        // Convert map values back to array
        const deduplicatedRecords = Array.from(uniqueRecordsMap.values());

        // Use the deduplicated data
        updatedData.extractedExcelData = deduplicatedRecords;
      } else {
        updatedData.extractedExcelData = extractedData.extractedExcelData;
      }

      // Store the original Excel data structure for display purposes
      updatedData.originalExcelData = extractedData.originalData;

      // If the extracted data includes a consumptionFileURL (S3 upload link),
      // add it to the survey data for processing
      if (extractedData.consumptionFileURL) {
        updatedData.consumptionFileURL = extractedData.consumptionFileURL;
      }

      // Set the combined data for processing
      setCombinedData(updatedData);

      // Hide the Excel data extractor and move to the processing step
      setShowExcelExtractor(false);
      setCurrentStep(1);

      const successMessage = isReuploadedFile
        ? "Reuploaded Excel file processed successfully"
        : "Excel data extracted and added to the survey submission";

      message.success(successMessage);
    } else {
      message.error("No survey data available");
    }
  };

  const handleExcelDataCancel = () => {
    // Hide the Excel data extractor
    setShowExcelExtractor(false);

    // Show a message that the Excel data extraction was skipped
    message.info("Excel data extraction skipped");

    // Still proceed with survey processing using just the survey data
    setCombinedData(surveyData);
    setCurrentStep(1);
  };

  // New handler for Excel validation errors
  const handleExcelError = (error: string) => {
    setExcelError(error);
    setShowReuploadModal(true);
    setShowExcelExtractor(false);
  };

  // Handle file reupload
  const handleReupload = (fileData: {
    name: string;
    type: string;
    content: string;
    occupancy_data?: boolean;
    isReuploaded?: boolean;
    consumptionFileURL?: string;
    originalData?: any;
    jsonData?: any;
    s3Upload?: any;
  }) => {
    if (surveyData) {
      // Create a clean copy of the survey data without any Excel data
      // Instead of using delete, create a new object with only the properties we want to keep
      const cleanSurveyData: any = {
        // Copy non-Excel-related properties only
        ...surveyData,
        // Initialize with an empty array to avoid type errors and override any Excel data
        location_consumption: [],
        // Remove any Excel-related properties
        extractedExcelData: undefined,
        originalExcelData: undefined,
        consumptionFileURL: undefined,
      };

      // Mark the uploaded file as a reupload
      const updatedFileData = {
        ...fileData,
        isReuploaded: true,
      };

      // Create fresh location_consumption array
      cleanSurveyData.location_consumption = [updatedFileData];

      // Update the survey data state with the clean version
      setSurveyData(cleanSurveyData);

      // Reset combinedData to avoid carrying over any previously processed Excel data
      setCombinedData(null);

      // Close the reupload modal and show the Excel extractor again
      setShowReuploadModal(false);
      setShowExcelExtractor(true);

      message.info("New Excel file uploaded. Validating...");
    }
  };

  // Cancel reupload and proceed without Excel data
  const handleReuploadCancel = () => {
    setShowReuploadModal(false);
    message.info("Excel data extraction skipped");

    // Proceed with survey processing using just the survey data without Excel data
    setCombinedData(surveyData);
    setCurrentStep(1);
  };

  const prepareSurveyData = (data: any) => {
    if (!data) return null;

    const numericFields = [
      "total_square_footage",
      "total_guest_rooms",
      "no_of_studios",
      "no_of_1_bedrooms",
      "no_of_2_bedrooms",
      "no_of_3_bedrooms",
      "rooms_floor_percentage",
      "common_areas_floor_percentage",
      "restaurants_floor_percentage",
      "spas_floor_percentage",
      "back_offices_floor_percentage",
      "other_areas_floor_percentage",
      "hvac_consumption_percentage",
      "split_unit_percentage",
      "vrv_vrf_percentage",
      "chiller_percentage",
      "boiler_percentage",
      "forecasted_occupancy_rate",
      "solar_energy_percentage",
      "other_renewable_energy_consumption_percentage",
    ];

    const booleanFields = [
      "has_energy_initiatives",
      "has_other_hvac_solutions",
      "has_major_bms_energy_equipment_change",
      "has_solar_energy_system_installed",
      "has_keycard_system",
    ];

    const arrayFields = ["gas_usage"];

    const joinArrayFields = ["hvac_system_type", "energy_initiatives", "hvac_solutions"];

    const validateNumeric = (value: any, defaultValue = 0): number => {
      if (value === undefined || value === null || value === "") return defaultValue;
      const num = parseFloat(String(value).replace(/,/g, ""));
      return Number.isNaN(num) ? defaultValue : num;
    };

    const validateBoolean = (value: any): boolean => {
      return value === true || value === "true" || value === 1 || value === "1";
    };

    const result: any = {
      ...data,
      existing_location_id: locationId,
      userId,
    };

    Object.keys(data).forEach((key) => {
      if (numericFields.includes(key)) {
        result[key] = validateNumeric(data[key]);
      } else if (booleanFields.includes(key)) {
        result[key] = validateBoolean(data[key]);
      } else if (arrayFields.includes(key)) {
        if (Array.isArray(data[key])) result[key] = data[key];
        else if (data[key]) result[key] = [data[key]];
        else result[key] = [];
      } else if (joinArrayFields.includes(key)) {
        if (Array.isArray(data[key])) result[key] = data[key].join(", ");
        else result[key] = String(data[key] || "");
      }
    });

    return result;
  };

  const renderStepContent = () => {
    if (showExcelExtractor && surveyData) {
      return (
        <ExcelDataExtractor
          jsonData={surveyData}
          onConfirm={handleExcelDataConfirm}
          onCancel={handleExcelDataCancel}
          onError={handleExcelError}
        />
      );
    }

    switch (currentStep) {
      case 0:
        if (!survey) {
          return (
            <div className="loading-container">
              <Spin size="large" tip="Loading survey..." />
            </div>
          );
        }
        return <Survey model={survey} />;
      case 1: {
        // Prepare the data with proper validation before passing to SurveyResponseProcessor
        const processData = prepareSurveyData(combinedData || surveyData);
        return <SurveyResponseProcessor surveyResponse={processData} onComplete={handleProcessingComplete} />;
      }
      default:
        return null;
    }
  };

  return (
    <Card className="onboarding-component">
      {isStartScreen ? (
        <Result
          status="info"
          title="Complete Onboarding"
          subTitle="To use Smart Building Energy Analytics, you need to complete the onboarding process."
          extra={
            <div className="onboarding-content">
              <Paragraph>
                Before you can analyze energy consumption data, we need some basic information about this location.
                Please complete a survey to provide details about the property, including:
              </Paragraph>
              <ul className="requirements-list">
                <li>
                  <span>Building size and layout</span>
                </li>
                <li>
                  <span>Occupancy patterns</span>
                </li>
                <li>
                  <span>Energy systems information</span>
                </li>
                <li>
                  <span>Billing periods</span>
                </li>
              </ul>
              <div className="action-button">
                <Button type="primary" size="large" onClick={handleStartSurvey} loading={loading}>
                  Start Onboarding
                </Button>
              </div>
            </div>
          }
        />
      ) : (
        <>
          <Title level={4}>Smart Building Energy Analytics Onboarding</Title>
          {!showExcelExtractor && !showReuploadModal && (
            <Steps current={currentStep} style={{ marginBottom: 24 }}>
              <Step title="Survey" description="Complete property details" />
              <Step title="Process" description="Save property information" />
            </Steps>
          )}
          {renderStepContent()}

          {/* Reupload Modal */}
          <ExcelReuploadModal
            visible={showReuploadModal}
            errorMessage={excelError}
            onReupload={handleReupload}
            onCancel={handleReuploadCancel}
          />
        </>
      )}
    </Card>
  );
};

export default OnboardingComponent;
